import { useEffect, useMemo, useRef, useState } from 'react';

import {
  CellStyleModule,
  type ColDef,
  ColumnApiModule,
  ColumnAutoSizeModule,
  type FirstDataRenderedEvent,
  type GridApi,
  type GridReadyEvent,
  ModuleRegistry,
  type RowSelectedEvent,
  themeQuartz
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { OverlaNoRowsTemplate, PaginableGrid } from 'app/components/grid/common';
import { usePaginateApplication } from 'app/hooks';
import { useSpinnerActions } from 'app/layouts/store';

import { gridExportExcel } from './grid-excel';
import { beautifyColumns } from './gridUtils';

export interface RowSelectionValue {
  isSelected?: boolean;
  data: any | Array<any>;
}

interface Props {
  toggleReload?: boolean;
  hideHeaders?: boolean;
  columnDefs: Array<ColDef>;
  url: string;
  search?: any;
  pageSize?: number;
  pagedTop?: boolean;
  data?: Array<any>;
  rowSelection?: 'single' | 'multiple';
  rowSelected?: (event: RowSelectionValue) => void;
  onGridReady?: (params: GridReadyEvent) => void;
  exportable?: boolean;
}

ModuleRegistry.registerModules([ColumnAutoSizeModule, ColumnApiModule, CellStyleModule]);

export const GridContainer = ({
  children,
  autoHeight = false
}: {
  children: any;
  autoHeight?: boolean;
}) => {
  return (
    <div
      className="ag-theme-alpine"
      style={
        autoHeight
          ? {}
          : {
              height: '500px'
            }
      }
    >
      {children}
    </div>
  );
};

export const OnlyGrid = ({
  domLayout = 'normal',
  hideHeaders,
  columnDefs,
  defaultColDef,
  data,
  rowSelection,
  rowSelected,

  onGridReady
}: {
  domLayout?: 'normal' | 'autoHeight' | 'print' | undefined;
  hideHeaders?: boolean;
  columnDefs: Array<ColDef>;
  defaultColDef?: ColDef;
  data?: Array<any>;
  onGridReady?: (params: GridReadyEvent) => void;
  rowSelection?: 'single' | 'multiple';
  rowSelected?: (event: RowSelectionValue) => void;
}) => {
  const gridApi = useRef<GridApi | null>(null);
  const [columns, setColumns] = useState(columnDefs);
  // const sizeToFit = () => {
  //   gridApi.current?.sizeColumnsToFit();
  // };

  const onLocalGridReady = (params: GridReadyEvent) => {
    gridApi.current = params.api;

    // sizeToFit();
    if (onGridReady) {
      onGridReady(params);
    }
  };

  /**
   * Fired the first time data is rendered into the grid.
   *
   * https://www.ag-grid.com/javascript-data-grid/grid-events/#reference-miscellaneous-firstDataRendered
   *
   * @param {FirstDataRenderedEvent} event
   * @return {*}
   */
  const onFirstDataRendered = (event?: FirstDataRenderedEvent) => {
    const gridApiLocal: GridApi | undefined = gridApi.current ?? undefined;
    if (gridApiLocal) {
      beautifyColumns(gridApiLocal);
    }
  };

  const baseColDef = useMemo(() => {
    return {
      resizable: true
    };
  }, []);

  return (
    <AgGridReact
      enableCellTextSelection={true}
      noRowsOverlayComponent={OverlaNoRowsTemplate}
      domLayout={domLayout}
      headerHeight={hideHeaders ? 0 : undefined}
      onGridReady={onLocalGridReady}
      columnDefs={columns}
      defaultColDef={defaultColDef ?? baseColDef}
      onFirstDataRendered={onFirstDataRendered}
      rowData={data}
      rowSelection={rowSelection}
      theme={themeQuartz}
      onRowSelected={(event: RowSelectedEvent) => {
        // if (event.node.isSelected()) {
        //   const selected = gridApi.current?.getSelectedRows();
        //   if (selected && selected?.length > 0) {
        //     console.log(selected);
        //   }
        // }
        if (rowSelected && rowSelection === 'single') {
          const rowsSelected = gridApi.current?.getSelectedRows();
          rowSelected({
            isSelected: event.node?.isSelected(),
            data: rowsSelected && rowsSelected?.length > 0 ? rowsSelected[0] : event.data
          });
        }
        if (rowSelected && rowSelection === 'multiple') {
          const rowsSelected = gridApi.current?.getSelectedRows();
          rowSelected({
            isSelected: event.node?.isSelected(),
            data: event.node?.isSelected() ? (rowsSelected ?? []) : event.data
          });
        }
      }}
      suppressMovableColumns={true}
    />
  );
};

function GridApplication({
  toggleReload,
  columnDefs,
  pagedTop,
  search,
  pageSize = 15,
  data,
  hideHeaders,
  url = '',
  rowSelection,
  rowSelected,
  onGridReady,
  exportable = true
}: Props) {
  const [firstRender, setFirstRender] = useState(false);
  const inMemory = data !== undefined;

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(pageSize);

  // ejecuta la busqueda invocando al servicio de la API
  const [{ data: paginable, loading, error }, execute] = usePaginateApplication<any>(
    url,
    page,
    perPage,
    search,
    inMemory
  );

  const { showSpinner, hideSpinner } = useSpinnerActions();

  useEffect(() => {
    if (loading !== undefined && loading) {
      showSpinner();
    } else {
      hideSpinner();
    }
  }, [loading]);

  // useEffect(() => {
  //   setPage(1);
  // }, [search]);

  // useEffect(() => {
  //   setFirstRender(true);
  // }, []);

  // useEffect(() => {
  //   if (firstRender) {
  //     execute();
  //   }
  // }, [toggleReload, execute]);

  useEffect(() => {
    if (!inMemory) {
      execute();
    }
  }, [page, perPage, execute, inMemory]);

  if (inMemory) {
    return <OnlyGrid data={data} hideHeaders={hideHeaders} columnDefs={columnDefs} />;
  }

  /**
   * Función encargada de realizar la exportación a excel del contenido de la grilla, invocando
   * al método correspondiente que procesa la definición de columnas y su posterior guardado
   * en un archivo Excel
   *
   */
  const exportGrid = async () => {
    await gridExportExcel(url, search, columnDefs);
  };

  return (
    <>
      {/*gridApplication*/}
      <GridContainer>
        <PaginableGrid
          exportable={exportable}
          // https://www.ag-grid.com/react-data-grid/excel-export/
          exportFn={exportGrid}
          onGridReady={onGridReady}
          rowSelection={rowSelection}
          rowSelected={rowSelected}
          pagedTop={pagedTop}
          data={loading || error ? ({ data: [] } as any) : paginable}
          columnDefs={columnDefs}
          hideHeaders={hideHeaders}
          page={page}
          setPage={setPage}
        />
      </GridContainer>
    </>
  );
}

export default GridApplication;
